/**
 * Shared helpers for ATS (`api_ats/v1`) and main API (`api/v1`) clients.
 * Both use {@link authInterceptor} which may **resolve** 4xx with a JSON body—callers must
 * assert or unwrap explicitly; we do not change interceptors here.
 */

/**
 * `authInterceptor` resolves many 4xx responses as a **fulfilled** promise with the JSON body.
 * Bodies like `{ status: 422, errors: ['err_…'] }` must be treated as errors before envelope unwrap
 * so TanStack Query mutations/queries surface `isError` and logs can include HTTP status.
 */
export function assertResolvedAtsNotHttpError(body: unknown): void {
  if (typeof body === 'string') {
    const snippet = body.trim().slice(0, 120);
    throw Object.assign(
      new Error(
        snippet
          ? `request_failed_non_json: ${snippet}`
          : 'request_failed_non_json'
      ),
      { status: 500, responseData: body }
    );
  }
  if (
    body !== null &&
    typeof body === 'object' &&
    'status' in body &&
    typeof (body as { status: unknown }).status === 'number' &&
    (body as { status: number }).status >= 400
  ) {
    const o = body as { status: number; errors?: string[] };
    const msg =
      Array.isArray(o.errors) && o.errors.length > 0
        ? String(o.errors[0])
        : `request_failed_status_${o.status}`;
    throw Object.assign(new Error(msg), {
      status: o.status,
      responseData: o,
    });
  }
}

/** Same predicate as {@link assertResolvedAtsNotHttpError} for `/api/v1` clients. */
export const assertResolvedApiNotHttpError = assertResolvedAtsNotHttpError;

/** @deprecated Use {@link assertResolvedAtsNotHttpError} */
export const throwIfResolvedAtsHttpFailure = assertResolvedAtsNotHttpError;

/**
 * Many ATS endpoints return `{ data: T, errors?: string[] }`. The axios client already returns
 * the parsed JSON body; this pulls out `data` when present.
 *
 * Rejects when `errors` is a non-empty array — even if `data` is absent.
 */
export function unwrapAtsResponseBody<T>(body: unknown): T {
  if (body !== null && typeof body === 'object') {
    const envelope = body as { data?: T; errors?: string[] };
    if (Array.isArray(envelope.errors) && envelope.errors.length > 0) {
      throw new Error(String(envelope.errors[0]));
    }
    if ('data' in envelope) {
      return envelope.data as T;
    }
  }
  return body as T;
}

/**
 * HTTP-failure assert (resolved 4xx / non-JSON) then {@link unwrapAtsResponseBody}.
 */
export function unwrapAtsEnvelope<T>(body: unknown): T {
  assertResolvedAtsNotHttpError(body);
  return unwrapAtsResponseBody<T>(body);
}

/**
 * The `ats-https` / `http` response interceptor returns the parsed JSON body (`response.data`),
 * but Axios typings still describe `Promise<AxiosResponse<T>>`.
 * Use on the result of `await http.get/post/put/...` before typing it.
 */
export function asAtsClientBody<T>(value: unknown): T {
  return value as T;
}
